import React from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { siteConfig } from '../../config/siteConfig';
import styled from 'styled-components';
import { Flex } from '../Flex/Flex';
import {
  Dimension,
  HandIcon,
  LineSvg,
  PointerSvg,
  RectSvg,
  TextIcon,
  PlusIcon,
} from '../Toolbar/SvgIcons';

const QuickHelpModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  text-align: left;
  color: var(--text);
  padding: 0rem 2rem 3rem 2rem;

  & h1 {
    margin-bottom: 0.2rem;
    font-weight: 550;
  }

  & p {
    padding: 0;
    color: var(--text-muted);
    font-size: 1rem;
    margin: 0;
    line-height: 160%;
  }

  & h2 {
    padding: 0;
    margin: 0.6rem 0 0.2rem 0;
    font-weight: 550;
  }

  & h4 {
    padding: 0;
    margin: 0.8rem 0 0 0;
    font-weight: 550;
  }

  & a {
    text-decoration: none;
  }

  & a:visited {
    color: var(--active-blue);
  }
`;

const ToolTable = styled.table`
  background-color: var(--surface-elevated);
  border: solid 1px var(--border);
  padding: 0;
  margin: 0.5rem 0;
  border-collapse: collapse;

  & td {
    padding: 0.2rem 1rem;
    border: solid 1px var(--border-light);
  }
`;

const ToolSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  margin: 1rem 0;
`;

const ToolIcon = styled.div`
  border: 1px solid var(--border);
  color: var(--text);
  background-color: var(--surface);
  border-radius: 2px;
  padding: 10px;
  min-width: 48px;
  min-height: 48px;
  font-size: 9px;
  font-weight: 700;
  text-align: center;
  padding: 0;

  display: flex;
  gap: 0.2rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const Kbd = styled.kbd`
  display: inline-block;
  background-color: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 0.8em;
  font-family: inherit;
  color: var(--text);
  white-space: nowrap;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

export const QuickHelModal = () => {
  const intl = useIntl();
  const kbd = (chunks: React.ReactNode) => <Kbd>{chunks}</Kbd>;
  return (
    <QuickHelpModalContainer>
      <Header>
        <Flex $flexDirection="column" $justifyContent="start">
          <h1>
            {intl.formatMessage(
              { id: 'quickHelp.title' },
              { appName: siteConfig.appName }
            )}
          </h1>
          <p>
            {intl.formatMessage({ id: 'quickHelp.readMorePre' })}{' '}
            <Link target="_blank" to="https://ritabygglov.se/guide">
              {intl.formatMessage({ id: 'quickHelp.readMoreLink' })}{' '}
            </Link>{' '}
            {intl.formatMessage({ id: 'quickHelp.readMorePost' })}
          </p>
          <p>
            {intl.formatMessage(
              { id: 'quickHelp.contact' },
              { contactEmail: siteConfig.contactEmail }
            )}
          </p>
        </Flex>
      </Header>
      <h2>{intl.formatMessage({ id: 'quickHelp.toolsHeading' })}</h2>
      <ToolTable>
        <tbody>
          <tr>
            <td>
              <ToolSection>
                <ToolIcon>
                  <HandIcon color={'#152437'} isFill={false} />
                  {intl.formatMessage({ id: 'quickHelp.panLabel' })}
                </ToolIcon>
                <p>
                  {intl.formatMessage({ id: 'quickHelp.panDesc' }, { kbd })}
                </p>
              </ToolSection>
            </td>
            <td>
              <ToolSection>
                <ToolIcon>
                  <PointerSvg color={'var(--text)'} isFill={true} />
                  {intl.formatMessage({ id: 'quickHelp.selectLabel' })}
                </ToolIcon>
                <p>
                  {intl.formatMessage({ id: 'quickHelp.selectDesc' }, { kbd })}
                </p>
              </ToolSection>
            </td>
          </tr>
          <tr>
            <td>
              <ToolSection>
                <ToolIcon>
                  <TextIcon color={'var(--text)'} isFill={false} />
                  {intl.formatMessage({ id: 'quickHelp.textLabel' })}
                </ToolIcon>
                <p>
                  {intl.formatMessage({ id: 'quickHelp.textDesc' }, { kbd })}
                </p>
              </ToolSection>
            </td>
            <td>
              <ToolSection>
                <ToolIcon>
                  <LineSvg color={'var(--text)'} isFill={true} />
                  {intl.formatMessage({ id: 'quickHelp.lineLabel' })}
                </ToolIcon>
                <p>
                  {intl.formatMessage({ id: 'quickHelp.lineDesc' }, { kbd })}
                </p>
              </ToolSection>
            </td>
          </tr>
          <tr>
            <td>
              <ToolSection>
                <ToolIcon>
                  <RectSvg color={'var(--text)'} isFill={true} />
                  {intl.formatMessage({ id: 'quickHelp.rectLabel' })}
                </ToolIcon>
                <p>
                  {intl.formatMessage({ id: 'quickHelp.rectDesc' }, { kbd })}
                </p>
              </ToolSection>
            </td>
            <td>
              <ToolSection>
                <ToolIcon>
                  <Dimension color={'var(--text)'} isFill={true} />
                  {intl.formatMessage({ id: 'quickHelp.dimensionLabel' })}
                </ToolIcon>
                <p>
                  {intl.formatMessage(
                    { id: 'quickHelp.dimensionDesc' },
                    { kbd }
                  )}
                </p>
              </ToolSection>
            </td>
          </tr>
          <tr>
            <td>
              <ToolSection>
                <ToolIcon>
                  <PlusIcon color={'var(--text)'} isFill={false} />
                  {intl.formatMessage({ id: 'quickHelp.addLabel' })}
                </ToolIcon>
                <p>{intl.formatMessage({ id: 'quickHelp.addDesc' })}</p>
              </ToolSection>
            </td>
          </tr>
        </tbody>
      </ToolTable>
      <Flex $flexDirection="column" $justifyContent="start" $alignItems="start">
        <h2>{intl.formatMessage({ id: 'quickHelp.actionsHeading' })}</h2>
        <h4>{intl.formatMessage({ id: 'quickHelp.zoomHeading' })}</h4>
        <p>{intl.formatMessage({ id: 'quickHelp.zoomDesc' }, { kbd })}</p>
        <h4>{intl.formatMessage({ id: 'quickHelp.deleteHeading' })}</h4>
        <p>{intl.formatMessage({ id: 'quickHelp.deleteDesc' }, { kbd })}</p>
        <h4>{intl.formatMessage({ id: 'quickHelp.undoHeading' })}</h4>
        <p>{intl.formatMessage({ id: 'quickHelp.undoDesc' }, { kbd })}</p>
        <h4>{intl.formatMessage({ id: 'quickHelp.copyHeading' })}</h4>
        <p>{intl.formatMessage({ id: 'quickHelp.copyDesc' }, { kbd })}</p>
        <h4>{intl.formatMessage({ id: 'quickHelp.groupHeading' })}</h4>
        <p>{intl.formatMessage({ id: 'quickHelp.groupDesc' }, { kbd })}</p>
        <h4>{intl.formatMessage({ id: 'quickHelp.snapLinesHeading' })}</h4>
        <p>{intl.formatMessage({ id: 'quickHelp.snapLinesDesc' }, { kbd })}</p>
        <h4>{intl.formatMessage({ id: 'quickHelp.importMapHeading' })}</h4>
        <p>{intl.formatMessage({ id: 'quickHelp.importMapDesc' })}</p>
        <h4>{intl.formatMessage({ id: 'quickHelp.exportHeading' })}</h4>
        <p>{intl.formatMessage({ id: 'quickHelp.exportDesc' })}</p>
        <h4>{intl.formatMessage({ id: 'quickHelp.saveHeading' })}</h4>
        <p>
          {intl.formatMessage(
            { id: 'quickHelp.saveDesc' },
            { appName: siteConfig.appName }
          )}
        </p>
      </Flex>
    </QuickHelpModalContainer>
  );
};
